import { namespace } from 'wsm-tagging-utils';

const runLinkTypeDecorator = (win) => {
	namespace(win, 'DDC.tracking.adobe_launch.decorators');

	const {
		DDC: { tracking }
	} = win;

	// ======================================================================
	// ---------------------------- Constructor -----------------------------
	// ======================================================================

	/**
	 * The Link Type Decorator updates link click events with the type of the
	 * link clicked and applies the type to the event to be used by event handlers.
	 *
	 * @param {Object} window The window object
	 * @param {tracking.framework.runtime.Page} page A Page instance for page related queries
	 * @param {tracking.framework.runtime.EventHandler} delegate The EventHandler implementation to which all interface operations are delegated
	 * @constructor
	 */
	tracking.adobe_launch.decorators.LinkTypeDecorator =
		tracking.adobe_launch.decorators.LinkTypeDecorator ||
		(function buildLinkTypeDecorator() {
			// ======================================================================
			// ------------------ Private Variables and Functions -------------------
			// ======================================================================

			// window.DDC = window.DDC || {};
			// window.DDC.WidgetData = window.DDC.WidgetData || {};
			// window.DDC.WidgetData.adobe_launch = window.DDC.WidgetData.adobe_launch || {};
			// window.DDC.WidgetData.adobe_launch.searchLinkClasses = window.DDC.WidgetData.adobe_launch.searchLinkClasses || '';

			/**
			 * A mapping of link types to matching selectors. Links clicked will be
			 * compared against these selectors and assigned a type. These types have
			 * been determined based on requirements from the event handler utilizing
			 * this decorator.
			 */
			const linkTypeSelectors = new Map([
				[
					'arrow.next',
					[
						'.previousNext a.next',
						"a[rel='next']",
						'.ddc-icon[class*=arrow-right]',
						'button.slick-next'
					]
				],
				[
					'arrow.prev',
					[
						'.previousNext a.prev',
						"a[rel='prev']",
						'.ddc-icon[class*=arrow-left]',
						'button.slick-prev'
					]
				],
				[
					'search.link',
					// for now, I'll just hardcode with value set in gm, cadillac and nissan variations:
					['.ddc-btn-internetprice', '.submit-search']
				],
				['image.link', ['a img', 'img']],
				['button.link', ['.btn', 'button']],
				['text.link', ['a']]
			]);

			// ======================================================================
			// ---------------------------- Constructor -----------------------------
			// ======================================================================

			class LinkTypeDecorator extends tracking.framework.runtime
				.EventHandlerDecorator {
				// constructor(window, page, delegate) {
				// 	super(window, page, delegate);
				// }

				// ======================================================================
				// --------------------------- Event Handlers ---------------------------
				// ======================================================================

				/**
				 * Provides supplementary functionality for the link click handler.
				 *
				 * @param {Object} eventData The event data object.
				 *
				 * Relevant eventData properties:
				 * {string} text Text on link
				 * {string} url The URL listed on "href"
				 * {string} pageName The name of the current page
				 * {Object} element A reference to the element clicked
				 */
				handleLinkClick(eventDataIn) {
					let eventData = eventDataIn;
					const { linkType, element } = eventData;
					if (!linkType) {
						eventData.linkType =
							LinkTypeDecorator.determineLinkType(element);

						if (
							$(element).closest(
								'[data-widget-name="ws-detailed-specs"], [data-widget-id="inventory-listing1"]'
							).length > 0
						) {
							eventData.uniqueEvent = 'detailed-specs-accordion';
							eventData.expand = LinkTypeDecorator.expanded(
								element,
								this.log
							);
						}

						eventData = Object.assign(
							eventData,
							LinkTypeDecorator.decorateSlideshow(
								eventData,
								this.log
							),
							LinkTypeDecorator.decorateShowroomLinks(
								eventData,
								this.log
							)
						);

						this.log.debug(`applied type "${eventData.linkType}"`);
					}

					this.delegate.handleLinkClick(eventData);
				}

				/**
				 * Parses information from the link clicked to determine a relevant
				 * link type.
				 *
				 * @param {Element} element A reference to the element clicked
				 * @returns {string} The type of link that was clicked.
				 */
				static determineLinkType(element) {
					if (element instanceof Element) {
						// iterate through link types to test element against
						// eslint-disable-next-line no-restricted-syntax, prefer-const
						for (let [linkType, selectors] of linkTypeSelectors) {
							// determine if element matches one of the selectors of this type
							for (let i = 0; i < selectors.length; i++) {
								const childElement = selectors[i]
									? element.querySelector(selectors[i])
									: null;
								const backgroundImage = window
									.getComputedStyle(element)
									.getPropertyValue('background-image');

								if (
									backgroundImage &&
									backgroundImage !== 'none'
								) {
									linkType = !element.classList.contains(
										'dropdown-toggle'
									)
										? 'image.link'
										: 'button.link';
								}

								if (
									childElement !== null ||
									(selectors[i] &&
										element.matches(selectors[i]))
								) {
									return linkType;
								}
							}
						}
					}

					return 'generic.link';
				}

				static determineSlidePositionPostClick($slide, linkType) {
					// slideshow default/grouped/carousel
					if (typeof $slide.data('slide-position') !== 'undefined') {
						// Carousel slideshow differs in that the current visible slide is still marked the active one
						// so the below logic finds the next/prev slide position and returns that.
						if (
							$slide.closest(
								'[data-widget-name="slideshow-carousel"]'
							).length > 0
						) {
							const total =
								LinkTypeDecorator.determineSlideTotal($slide);
							const slidePositionPreClick =
								$slide.data('slide-position');

							if (linkType.includes('next')) {
								return slidePositionPreClick === total
									? 1
									: slidePositionPreClick + 1;
							} else {
								return slidePositionPreClick === 1
									? total
									: slidePositionPreClick - 1;
							}
						}

						return parseInt($slide.data('slide-position'), 10);
					}

					// slideshow background - slick
					if (typeof $slide.data('slick-index') !== 'undefined') {
						return parseInt($slide.data('slick-index'), 10) + 1;
					}

					return $slide.index() + 1;
				}

				static determineSlideTotal($slide) {
					let $siblings;

					// The grouped slideshow has data groups which repeats the slides multiple times
					// Users then uses tabs to select the different groupings

					if (typeof $slide.data('group') !== 'undefined') {
						const group = $slide.data('group');
						$siblings = $slide.siblings(`[data-group="${group}"]`); // how many slides are in the current slides group?
					} else {
						$siblings = $slide.siblings();
					}

					// The carousel slideshow has cloned elements that should not be part of the count
					return $siblings.not('.clone').length + 1;
				}

				static decorateSlideshow({ linkType, element }, log) {
					if (linkType.includes('arrow')) {
						const decorations = {};
						const $widget =
							$(element).closest('[data-widget-name]');
						/*
						slick-current - slideshow background - slick
						cycle-slide-active - slideshow default|grouped
						activeSlidePostClick will be set to whatever slide shows after clicking the arrow. This design decision
						is due to how the background-slick and default slideshows mark their active slide at this point
						in the code execution.
						So if you update this to support a new slideshow be sure the currentSlide is the slide post arrow
						click!
						*/
						const $activeSlidePostClick = $widget.find(
							'.slick-current,.cycle-slide-active,.active'
						);

						if ($activeSlidePostClick.length > 0) {
							decorations.slidePositionPostClick =
								LinkTypeDecorator.determineSlidePositionPostClick(
									$activeSlidePostClick,
									linkType
								);
							log.debug(
								`applied slide position "${decorations.slidePositionPostClick}"`
							);
							decorations.slideTotal =
								LinkTypeDecorator.determineSlideTotal(
									$activeSlidePostClick
								);
							log.debug(
								`applied slide total "${decorations.slideTotal}"`
							);
						}

						decorations.slideControlLabel = $(element).text();
						return decorations;
					}
					return {};
				}

				static decorateShowroomLinks({ element }, log) {
					const $showroomDetailsSection = $(element).closest(
						'.showroom-details-section'
					);

					/* jQuery looks to have a synthetic click as part of initalizing ui-tabs.  
					This is here so an attribute can be set to indicate the inital load of the widget and ignore the first "click", 
					clicking any other child will unset the attribute. */
					// /htdocs/v9/widgets/showroom/default/v2/components/tech-specs/component.vm
					// /htdocs/v9/viewmodel/widgets/tracking/adobe_launch/decorators/LinkTypeDecorator.js
					$(element)
						.closest('.showroom-detail')
						.find('[ui-tabs-initial-load]')
						.attr('ui-tabs-initial-load', false);

					if ($showroomDetailsSection.length > 0) {
						const decorations = {};
						const heading = $showroomDetailsSection
							.find('h4')
							.text();
						decorations.showroomSectionHeading = heading;
						decorations.uniqueEvent = 'showroom-toggle';
						log.debug(
							`applied showroom section heading "${heading}"`
						);
						return decorations;
					}

					return {};
				}

				static expanded(element, log) {
					// This may look backwards. That's because the expansion happens after this point in the code
					// execution. So if the element parent has collapsed on it that means it is about to be expand.
					// So we pass expand is true!
					let expand = element.classList.contains('collapsed');
					if (!expand) {
						expand = !element
							.closest('.panel-heading')
							.nextElementSibling.classList.contains('in');
					}

					log.debug(`applied eventData expanded "${expand}"`);
					return expand.toString();
				}
			}

			LinkTypeDecorator.prototype.logCategory = 'LinkTypeDecorator';

			return LinkTypeDecorator;
		})();
};

export default runLinkTypeDecorator;
